import { useState } from 'react';
import { Button, Table, Tag, Typography } from 'antd';
import { ConfirmationPopup, ItemImage } from '../../components';
import { InventoryReplenishmentWavesListHook } from '../../hooks/inventory';
import { SuggestedTransferQuantityModal } from './suggestedTransferQuantityModal';
import {
  getTransferAlertMessage,
  TransferAlertIndicator,
} from './transferAlertIndicator';
import {
  CommonUtility,
  DateFormat,
  DateUtility,
  InventoryService,
  ToastMessage,
  theme,
} from '../../utility';

const { Text } = Typography;

const statusColor = {
  draft: theme.colors.grey,
  pending: theme.colors.yellow,
};

const lineStatusPillColor = status => {
  const key = (status || '').toLowerCase();
  return statusColor[key] ?? theme.colors.grey;
};

const getLineStatus = line => line.replenishmentLineStatus || line.status || '';

const isPendingLine = line => getLineStatus(line).toLowerCase() === 'pending';

const getWaveAlertMessages = wave => [
  ...new Set((wave.lines || []).map(getTransferAlertMessage).filter(Boolean)),
];

export function InventoryTransferWavesListScreen({ warehouseId }) {
  const { data, loading, error, totalData, filter, filterChanged, pageChanged, refresh } =
    InventoryReplenishmentWavesListHook(warehouseId);

  const [pendingLineDelete, setPendingLineDelete] = useState(null);
  const [itemToEditQuantity, setItemToEditQuantity] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  const openDeleteLineConfirm = (waveId, line) => {
    setPendingLineDelete({
      waveId,
      lineId: line._id,
      warehouseId,
      sku: line.productId?.p_sku,
      title: line.productId?.p_title,
    });
  };

  const closeDeleteLineConfirm = () => {
    if (!deleteProcessing) setPendingLineDelete(null);
  };

  const confirmDeleteWaveLine = async () => {
    if (!pendingLineDelete) return;
    try {
      setDeleteProcessing(true);
      await InventoryService.deleteReplenishmentWaveLine(
        pendingLineDelete.waveId,
        pendingLineDelete.lineId,
        pendingLineDelete.warehouseId,
      );
      ToastMessage.success('Transfer line removed from the wave.');
      setPendingLineDelete(null);
      refresh();
    } catch (e) {
      ToastMessage.apiError(e?.error || e);
    } finally {
      setDeleteProcessing(false);
    }
  };

  const lineColumns = waveId => [
    {
      title: 'Product',
      key: 'product',
      render: (_, line) => (
        <div className="d-flex align-items-center">
          <ItemImage
            imageKey="p_images"
            displayImageKey="display_image"
            item={line.productId}
            size="mini"
          />
          <div className="ml-4">
            <Text strong>{line.productId?.p_title || '—'}</Text>
            <div className="d-flex">
              <Text type="secondary" className="mr-2">
                SKU
              </Text>
              <Text strong>{line.productId?.p_sku || '—'}</Text>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'From',
      key: 'from',
      render: (_, line) => (
        <div>
          <Tag color={line.sourceLocationId?.zoneId?.color}>
            {line.sourceLocationId?.zoneId?.code || '—'}
          </Tag>
          <br />
          <Tag className="mt-1" color={line.sourceLocationId?.zoneId?.color}>
            {line.sourceLocationId?.code || '—'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'To',
      key: 'to',
      render: (_, line) => {
        if (!line.destinationLocationId) {
          return <Text type="secondary">No location assigned — scan open location</Text>;
        }
        return (
          <div>
            <Tag color={line.destinationLocationId?.zoneId?.color}>
              {line.destinationLocationId?.zoneId?.code || '—'}
            </Tag>
            <br />
            <Tag className="mt-1" color={line.destinationLocationId?.zoneId?.color}>
              {line.destinationLocationId?.code || '—'}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      render: (_, line) => (
        <>
          <div className="d-flex align-items-center">
            <Text>{line.suggestedQuantity}</Text>
            <Text type="secondary" className="ml-1">
              Units
            </Text>
            <TransferAlertIndicator
              className="ml-1"
              message={getTransferAlertMessage(line)}
            />
          </div>
          {line.suggestedQuantityCartons ? (
            <div>
              <Text>{line.suggestedQuantityCartons}</Text>
              <Text type="secondary" className="ml-1">
                Cartons
              </Text>
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, line) => {
        const lineStatus = getLineStatus(line);
        return (
          <Tag color={lineStatusPillColor(lineStatus)}>
            {CommonUtility.removeUnderscoreAndCapitalize(lineStatus || '—')}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, line) => (
        <div className="d-flex justify-content-end">
          <Button
            type="link"
            disabled={!isPendingLine(line)}
            onClick={() => setItemToEditQuantity(line)}
          >
            Edit
          </Button>
          <Button danger type="link" onClick={() => openDeleteLineConfirm(waveId, line)}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: 'Wave',
      key: 'wave',
      render: (_, wave) => {
        const alertMessages = getWaveAlertMessages(wave);

        return (
          <>
            <div className="d-flex align-items-center">
              <Text strong>{wave.waveNumber || '—'}</Text>
              <TransferAlertIndicator className="ml-1" messages={alertMessages} />
            </div>
            {wave.waveKind ? (
              <div>
                <Text type="secondary" className="text-capitalize">
                  {wave.waveKind}
                </Text>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, wave) => {
        const statusKey = (wave.status || '').toLowerCase();
        return (
          <Tag color={statusColor[statusKey] ?? theme.colors.primary}>
            {CommonUtility.toTitleCase(wave.status || '')}
          </Tag>
        );
      },
    },
    {
      title: 'Transfers',
      key: 'transfers',
      dataIndex: 'lines',
      render: lines => lines?.length ?? 0,
    },
    {
      title: 'Updated',
      key: 'updated',
      render: (_, wave) =>
        wave.updatedAt
          ? DateUtility.formatDate(wave.updatedAt, DateFormat.dateTime)
          : '—',
    },
  ];

  const pagination = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: total => `${total} transfer batches`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  const deleteMessage =
    pendingLineDelete &&
    `Remove this transfer from the wave?${
      pendingLineDelete.sku || pendingLineDelete.title
        ? ` (${[pendingLineDelete.sku, pendingLineDelete.title].filter(Boolean).join(' — ')})`
        : ''
    }`;

  return (
    <div>
      {error ? (
        <div className="mb-2 text-danger" role="alert">
          Unable to load transfer waves.
        </div>
      ) : null}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        locale={{ emptyText: 'No transfer waves yet' }}
        style={{
          borderRadius: 6,
          border: `1px solid ${theme.colors.semanticBorder}`,
        }}
        expandable={{
          expandedRowRender: wave => (
            <Table
              rowKey="_id"
              columns={lineColumns(wave._id)}
              dataSource={wave.lines || []}
              pagination={false}
              size="small"
            />
          ),
          rowExpandable: wave => (wave.lines?.length ?? 0) > 0,
        }}
      />
      <ConfirmationPopup
        open={Boolean(pendingLineDelete)}
        onClose={closeDeleteLineConfirm}
        onConfirm={confirmDeleteWaveLine}
        loading={deleteProcessing}
        title="Delete transfer line"
        message={deleteMessage || ''}
        confirmText="Delete"
      />
      <SuggestedTransferQuantityModal
        open={!!itemToEditQuantity}
        item={itemToEditQuantity}
        onClose={() => setItemToEditQuantity(null)}
        onUpdated={refresh}
      />
    </div>
  );
}
